import { useState } from "react";
import { toast, Toaster } from "sonner";
import { CalendarRange, CreditCard } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { HostawayCalendars } from "./HostawayWidget";
import AmenityList from "./AmenityList";
import { useLang } from "@/i18n";

export default function Booking() {
  const { t } = useLang();
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [guests, setGuests] = useState(2);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [listingId, setListingId] = useState(538706);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes("@") || !checkin || !checkout) {
      toast.error(t.booking.error);
      return;
    }
    setSending(true);
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api/booking-requests`;
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, checkin, checkout, guests }),
      });
      if (!res.ok) throw new Error();
      toast.success(t.booking.success, { style: { background: "#162B21", color: "#F9F8F3", border: "1px solid rgba(194,166,117,0.4)" } });
      setForm({ name: "", email: "", phone: "", message: "" });
      setCheckin("");
      setCheckout("");
    } catch {
      toast.error(t.booking.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border gold-hairline px-4 py-3 text-sm text-bone placeholder:text-sage/50 focus:outline-none focus:border-gold transition-colors duration-300";

  return (
    <section
      id="reservations"
      data-testid="booking-section"
      className="py-24 sm:py-32 px-5 sm:px-10"
    >
      <Toaster position="bottom-right" />
      <div className="mx-auto max-w-[1500px]">
        <SectionHeader eyebrow={t.booking.eyebrow} title={t.booking.title} />
        <Reveal delay={0.1}>
          <p className="mt-6 max-w-xl text-sm sm:text-base text-mist font-light leading-relaxed">
            {t.booking.intro}
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div
            className="mt-8 border-l-2 border-gold/70 pl-5 sm:pl-6 max-w-xl"
            data-testid="booking-privacy"
          >
            <p className="font-display italic text-lg sm:text-xl text-bone/90 font-light leading-snug">
              {t.booking.privacy}
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-14 mt-12 items-start">
          <Reveal delay={0.15}>
            <div data-testid="booking-by-lodging">
              <h3 className="font-display text-2xl text-bone flex items-center gap-3">
                <CalendarRange size={20} className="text-gold shrink-0" aria-hidden="true" />
                {t.booking.calendarTitle}
              </h3>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-goldlight mt-4 leading-relaxed">
                {t.booking.calendarNote}
              </p>
              <div className="mt-6">
                <HostawayCalendars listingId={listingId} onListingChange={setListingId} />
              </div>
              <AmenityList listingId={listingId} />
              <p
                className="mt-5 font-mono text-[0.55rem] uppercase tracking-[0.2em] text-sage leading-relaxed"
                data-testid="booking-combo-note"
              >
                {t.booking.comboNote}
              </p>
            </div>
          </Reveal>

          <div className="space-y-12 lg:pt-2">
            <Reveal delay={0.2}>
              <div data-testid="booking-terms">
                <h3 className="font-display text-2xl text-bone flex items-center gap-3">
                  <CreditCard size={20} className="text-gold shrink-0" aria-hidden="true" />
                  {t.booking.paymentTitle}
                </h3>
                <ul className="mt-6 space-y-3">
                  {t.booking.payments.map((p, i) => (
                    <li
                      key={i}
                      className="flex gap-4 text-xs sm:text-sm text-sage font-light leading-relaxed"
                    >
                      <span className="text-gold font-mono">·</span>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <div
                data-testid="booking-request-form"
                className="glass p-8 sm:p-10"
              >
                <h3 className="font-display text-2xl sm:text-3xl font-light text-bone">
                  {t.booking.formTitle}
                </h3>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em] text-gold mt-3">
                  {t.booking.formNote}
                </p>
                <form onSubmit={submit} className="mt-8 space-y-5">
                  <input
                    data-testid="booking-form-name"
                    className={inputCls}
                    placeholder={t.booking.name}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                  <input
                    data-testid="booking-form-email"
                    type="email"
                    className={inputCls}
                    placeholder={t.booking.email}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                  <input
                    data-testid="booking-form-phone"
                    type="tel"
                    className={inputCls}
                    placeholder={t.booking.phone}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      data-testid="booking-form-checkin"
                      type="date"
                      className={`${inputCls} [color-scheme:dark]`}
                      value={checkin}
                      onChange={(e) => setCheckin(e.target.value)}
                      required
                    />
                    <input
                      data-testid="booking-form-checkout"
                      type="date"
                      className={`${inputCls} [color-scheme:dark]`}
                      value={checkout}
                      onChange={(e) => setCheckout(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <span className="font-mono text-[0.55rem] uppercase tracking-[0.25em] text-sage block mb-2">
                      {t.booking.guestsField}
                    </span>
                    <div className="flex items-center border gold-hairline w-40">
                      <button
                        type="button"
                        data-testid="booking-form-guests-minus"
                        aria-label="-"
                        onClick={() => setGuests((g) => Math.max(1, g - 1))}
                        className="px-4 py-3 text-gold hover:bg-moss/60 transition-colors"
                      >
                        −
                      </button>
                      <span
                        data-testid="booking-form-guests-count"
                        className="flex-1 text-center text-sm text-bone"
                      >
                        {guests}
                      </span>
                      <button
                        type="button"
                        data-testid="booking-form-guests-plus"
                        aria-label="+"
                        onClick={() => setGuests((g) => Math.min(16, g + 1))}
                        className="px-4 py-3 text-gold hover:bg-moss/60 transition-colors"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <textarea
                    data-testid="booking-form-message"
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder={t.booking.message}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                  <button
                    type="submit"
                    data-testid="booking-form-submit"
                    disabled={sending}
                    className="w-full bg-gold text-night hover:bg-goldlight disabled:opacity-50 px-8 py-4 font-mono text-[0.65rem] uppercase tracking-[0.22em] transition-all duration-300"
                  >
                    {sending ? "…" : t.booking.submit}
                  </button>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
